import {
  ActionIcon,
  Alert,
  Badge,
  Breadcrumbs,
  Button,
  Center,
  Checkbox,
  Group,
  List,
  Loader,
  Modal,
  NumberInput,
  Paper,
  PasswordInput,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCheck,
  IconDeviceFloppy,
  IconExclamationCircle,
  IconLock,
  IconMail,
  IconMessageCircle,
  IconRefresh,
  IconSend,
  IconShieldCheck,
} from "@tabler/icons-react";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";

import {
  useAdminNotificationSettings,
  useTestAdminNotificationSettings,
  useUpdateAdminNotificationSettings,
} from "@/api/queries";
import type {
  AdminNotificationSettings,
  AdminNotificationTestResult,
  AdminNotificationSettingsUpdateBody,
  NotificationTlsMinVersion,
} from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorBanner } from "@/components/ErrorBanner";
import { NotFoundPage } from "@/pages/NotFoundPage";

const ROUTING_ROWS = [
  {
    severity: "critical",
    receivers: "Email + Telegram",
    firstNotice: "10s",
    batch: "5m",
    repeat: "30m",
    note: "Telegram notifies loudly",
  },
  {
    severity: "warning",
    receivers: "Email + Telegram",
    firstNotice: "30s",
    batch: "10m",
    repeat: "4h",
    note: "Telegram is silent by default",
  },
  {
    severity: "info / default",
    receivers: "Email",
    firstNotice: "5m",
    batch: "30m",
    repeat: "24h",
    note: "Digest-style routing",
  },
];

type FormValues = {
  smtp_host: string;
  smtp_port: number | string;
  from_address: string;
  recipients: string;
  auth_username: string;
  smtp_auth_password: string;
  clear_smtp_auth_password: boolean;
  require_tls: boolean;
  tls_min_version: NotificationTlsMinVersion;
  telegram_chat_id: string;
  telegram_bot_token: string;
  clear_telegram_bot_token: boolean;
  telegram_warning_silent: boolean;
};

const DEFAULT_FORM: FormValues = {
  smtp_host: "",
  smtp_port: 587,
  from_address: "",
  recipients: "",
  auth_username: "",
  smtp_auth_password: "",
  clear_smtp_auth_password: false,
  require_tls: true,
  tls_min_version: "TLS12",
  telegram_chat_id: "",
  telegram_bot_token: "",
  clear_telegram_bot_token: false,
  telegram_warning_silent: true,
};

const CHAT_ID_PATTERN = /^-?[0-9]+$/u;

export function AdminNotificationsPage() {
  const auth = useAuth();
  const isAdmin = auth.status === "authenticated" && auth.profile.roles.includes("admin");

  if (!isAdmin) {
    return <NotFoundPage />;
  }

  return <AdminNotificationsContent />;
}

function AdminNotificationsContent() {
  const settingsQuery = useAdminNotificationSettings();
  const updateMutation = useUpdateAdminNotificationSettings();
  const testMutation = useTestAdminNotificationSettings();
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [values, setValues] = useState<FormValues>(DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<AdminNotificationTestResult | null>(null);

  useEffect(() => {
    if (settingsQuery.data) {
      setValues(toForm(settingsQuery.data));
      setFormError(null);
    }
  }, [settingsQuery.data]);

  const set = <K extends keyof FormValues>(field: K, value: FormValues[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setFormError(null);
    setTestResult(null);
    updateMutation.reset();
    testMutation.reset();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validate(values);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const body = toUpdateBody(values);
    try {
      const updated = await updateMutation.mutateAsync(body);
      setValues(toForm(updated));
      setTestResult(null);
    } catch {
      // Error is rendered from updateMutation.error.
    }
  };

  const handleSendTest = async () => {
    setFormError(null);
    setTestResult(null);
    try {
      setTestResult(await testMutation.mutateAsync());
    } catch {
      // Error is rendered from testMutation.error.
    }
  };

  const settings = settingsQuery.data;
  const hasReadyChannel = Boolean(settings?.email_enabled || settings?.telegram_enabled);

  return (
    <Stack>
      <Breadcrumbs>
        <Text>Admin</Text>
        <Text>Notifications</Text>
      </Breadcrumbs>

      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="xs">
            <Title order={1}>Notifications</Title>
            <Tooltip label="Notification setup help">
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label="Notification setup help"
                onClick={() => setInstructionsOpen(true)}
              >
                <IconExclamationCircle size={20} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Text c="dimmed" size="sm">
            Email and Telegram delivery settings for alarm notifications.
          </Text>
        </Stack>
        <Group>
          <Tooltip label="Refresh settings">
            <ActionIcon
              variant="default"
              size="lg"
              onClick={() => settingsQuery.refetch()}
              loading={settingsQuery.isFetching}
              aria-label="Refresh settings"
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Badge color="green" variant="light" leftSection={<IconShieldCheck size={12} />}>
            Secrets encrypted
          </Badge>
        </Group>
      </Group>

      <Paper withBorder p="md" radius="md">
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
          <SetupFact
            icon={<IconMail size={18} />}
            title="Email"
            body="Use a dedicated service mailbox with authenticated sending."
          />
          <SetupFact
            icon={<IconMessageCircle size={18} />}
            title="Telegram"
            body="Use an operations chat where the EMS bot has already been added."
          />
          <SetupFact
            icon={<IconLock size={18} />}
            title="Saved Safely"
            body="Passwords and bot tokens are write-only and never shown after saving."
          />
        </SimpleGrid>
      </Paper>

      {settingsQuery.error && <ErrorBanner error={settingsQuery.error} />}

      <Paper withBorder p="lg" radius="md">
        <form onSubmit={handleSubmit} noValidate>
          <Stack gap="lg">
            <Group justify="space-between" align="flex-start">
              <Stack gap={2}>
                <Title order={2}>Delivery Settings</Title>
                <Text c="dimmed" size="sm">
                  Last saved: {settings?.updated_at ? formatTimestamp(settings.updated_at) : "not saved yet"}
                </Text>
              </Stack>
              <Group>
                <StatusBadge ready={Boolean(settings?.email_enabled)} label="Email" />
                <StatusBadge ready={Boolean(settings?.telegram_enabled)} label="Telegram" />
              </Group>
            </Group>

            {settingsQuery.isLoading ? (
              <Center py="xl">
                <Loader />
              </Center>
            ) : (
              <>
                {formError && (
                  <Alert
                    icon={<IconAlertCircle size={18} />}
                    color="red"
                    variant="light"
                    title="Check the settings"
                  >
                    {formError}
                  </Alert>
                )}
                {updateMutation.error && (
                  <ErrorBanner error={updateMutation.error} title="Save failed" />
                )}
                {testMutation.error && (
                  <ErrorBanner error={testMutation.error} title="Test failed" />
                )}
                {testResult && <TestResultAlert result={testResult} />}

                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
                  <Stack>
                    <Group justify="space-between" align="center">
                      <Title order={3}>Email Delivery</Title>
                      <StatusBadge ready={Boolean(settings?.email_enabled)} label="Email" />
                    </Group>
                    <TextInput
                      label="SMTP server"
                      placeholder="smtp.example.com"
                      value={values.smtp_host}
                      onChange={(event) => set("smtp_host", event.currentTarget.value)}
                    />
                    <NumberInput
                      label="Port"
                      min={1}
                      max={65535}
                      value={values.smtp_port}
                      onChange={(value) => set("smtp_port", value)}
                    />
                    <TextInput
                      label="From address"
                      placeholder="ems-alerts@example.com"
                      value={values.from_address}
                      onChange={(event) => set("from_address", event.currentTarget.value)}
                    />
                    <TextInput
                      label="Send alerts to"
                      description="Use commas between multiple email addresses."
                      placeholder="ops@example.com, manager@example.com"
                      value={values.recipients}
                      onChange={(event) => set("recipients", event.currentTarget.value)}
                    />
                    <TextInput
                      label="Username"
                      placeholder="ems-alerts@example.com"
                      value={values.auth_username}
                      onChange={(event) => set("auth_username", event.currentTarget.value)}
                    />
                    <PasswordInput
                      label="Password"
                      description={
                        settings?.smtp_auth_password_set
                          ? "Saved securely. Leave blank to keep the current password."
                          : "Stored securely after saving."
                      }
                      placeholder={
                        settings?.smtp_auth_password_set
                          ? "Saved password"
                          : "Enter password"
                      }
                      value={values.smtp_auth_password}
                      disabled={values.clear_smtp_auth_password}
                      onChange={(event) =>
                        set("smtp_auth_password", event.currentTarget.value)
                      }
                    />
                    {settings?.smtp_auth_password_set && (
                      <Checkbox
                        label="Remove saved password on save"
                        checked={values.clear_smtp_auth_password}
                        onChange={(event) =>
                          set("clear_smtp_auth_password", event.currentTarget.checked)
                        }
                      />
                    )}
                    <Switch
                      label="Require secure connection"
                      checked={values.require_tls}
                      onChange={(event) => set("require_tls", event.currentTarget.checked)}
                    />
                    <Select
                      label="Minimum security"
                      data={[
                        { value: "TLS12", label: "TLS 1.2 or newer" },
                        { value: "TLS13", label: "TLS 1.3 only" },
                      ]}
                      value={values.tls_min_version}
                      onChange={(value) =>
                        set("tls_min_version", (value ?? "TLS12") as NotificationTlsMinVersion)
                      }
                    />
                  </Stack>

                  <Stack>
                    <Group justify="space-between" align="center">
                      <Title order={3}>Telegram Delivery</Title>
                      <StatusBadge
                        ready={Boolean(settings?.telegram_enabled)}
                        label="Telegram"
                      />
                    </Group>
                    <PasswordInput
                      label="Bot token"
                      description={
                        settings?.telegram_bot_token_set
                          ? "Saved securely. Leave blank to keep the current token."
                          : "Created in Telegram through BotFather."
                      }
                      placeholder={
                        settings?.telegram_bot_token_set
                          ? "Saved bot token"
                          : "Enter bot token"
                      }
                      value={values.telegram_bot_token}
                      disabled={values.clear_telegram_bot_token}
                      onChange={(event) =>
                        set("telegram_bot_token", event.currentTarget.value)
                      }
                    />
                    {settings?.telegram_bot_token_set && (
                      <Checkbox
                        label="Remove saved bot token on save"
                        checked={values.clear_telegram_bot_token}
                        onChange={(event) =>
                          set("clear_telegram_bot_token", event.currentTarget.checked)
                        }
                      />
                    )}
                    <TextInput
                      label="Chat ID"
                      description="Numeric group or chat ID for the operations chat."
                      placeholder="-1001234567890"
                      value={values.telegram_chat_id}
                      onChange={(event) => set("telegram_chat_id", event.currentTarget.value)}
                    />
                    <Switch
                      label="Send warning messages silently"
                      description="Critical alerts still notify loudly."
                      checked={values.telegram_warning_silent}
                      onChange={(event) =>
                        set("telegram_warning_silent", event.currentTarget.checked)
                      }
                    />
                  </Stack>
                </SimpleGrid>

                <Group justify="flex-end">
                  <Button
                    type="button"
                    variant="default"
                    leftSection={<IconSend size={16} />}
                    loading={testMutation.isPending}
                    disabled={!hasReadyChannel || updateMutation.isPending}
                    onClick={handleSendTest}
                  >
                    Send test
                  </Button>
                  <Button
                    type="submit"
                    leftSection={<IconDeviceFloppy size={16} />}
                    loading={updateMutation.isPending}
                  >
                    Save settings
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </form>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Title order={2}>Severity Routing</Title>
          <ScrollArea>
            <Table miw={720} verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Severity</Table.Th>
                  <Table.Th>Receivers</Table.Th>
                  <Table.Th>First notice</Table.Th>
                  <Table.Th>Batch interval</Table.Th>
                  <Table.Th>Repeat</Table.Th>
                  <Table.Th>Policy</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {ROUTING_ROWS.map((row) => (
                  <Table.Tr key={row.severity}>
                    <Table.Td>
                      <Badge variant="light" color={severityColor(row.severity)}>
                        {row.severity}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{row.receivers}</Table.Td>
                    <Table.Td>{row.firstNotice}</Table.Td>
                    <Table.Td>{row.batch}</Table.Td>
                    <Table.Td>{row.repeat}</Table.Td>
                    <Table.Td>{row.note}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Stack>
      </Paper>

      <Modal
        opened={instructionsOpen}
        onClose={() => setInstructionsOpen(false)}
        title="Notification setup help"
        size="lg"
      >
        <Stack>
          <Text size="sm">
            Use these fields to save the delivery details an administrator gets
            from the mail provider and from Telegram. Secret values are
            encrypted by EMS and are not displayed again after saving.
          </Text>
          <List spacing="sm" size="sm">
            <List.Item>
              For email, use a dedicated service mailbox. Fill in the mail
              server, port, sender address, receivers, username, password, and
              secure connection preference from the provider or onsite relay.
            </List.Item>
            <List.Item>
              For Telegram, create the bot with BotFather, add it to the
              operations chat, and enter the bot token plus the numeric chat ID.
            </List.Item>
            <List.Item>
              Critical alerts repeat every 30 minutes and notify loudly.
              Warning alerts repeat every 4 hours and are silent in Telegram
              by default. Info alerts go to email only.
            </List.Item>
          </List>
        </Stack>
      </Modal>
    </Stack>
  );
}

function TestResultAlert({ result }: { result: AdminNotificationTestResult }) {
  const hasFailure = result.email.status === "failed" || result.telegram.status === "failed";
  const color = hasFailure ? "red" : result.ok ? "green" : "yellow";
  const title = hasFailure ? "Test needs attention" : result.ok ? "Test sent" : "Nothing sent";
  return (
    <Alert color={color} variant="light" title={title}>
      <List size="sm" spacing={4}>
        <List.Item>Email: {result.email.message}</List.Item>
        <List.Item>Telegram: {result.telegram.message}</List.Item>
      </List>
    </Alert>
  );
}

function StatusBadge({ ready, label }: { ready: boolean; label: string }) {
  return (
    <Badge
      color={ready ? "green" : "gray"}
      variant="light"
      leftSection={ready ? <IconCheck size={12} /> : undefined}
    >
      {label} {ready ? "ready" : "needs setup"}
    </Badge>
  );
}

function SetupFact({
  icon,
  title,
  body,
}: {
  icon: ReactNode;
  title: string;
  body: string;
}) {
  return (
    <Group align="flex-start" wrap="nowrap">
      <ThemeIcon variant="light" color="indigo">
        {icon}
      </ThemeIcon>
      <Stack gap={2}>
        <Text fw={600}>{title}</Text>
        <Text c="dimmed" size="sm">
          {body}
        </Text>
      </Stack>
    </Group>
  );
}

function toForm(settings: AdminNotificationSettings): FormValues {
  return {
    smtp_host: settings.smtp_host,
    smtp_port: settings.smtp_port,
    from_address: settings.from_address,
    recipients: settings.recipients,
    auth_username: settings.auth_username,
    smtp_auth_password: "",
    clear_smtp_auth_password: false,
    require_tls: settings.require_tls,
    tls_min_version: settings.tls_min_version,
    telegram_chat_id: settings.telegram_chat_id,
    telegram_bot_token: "",
    clear_telegram_bot_token: false,
    telegram_warning_silent: settings.telegram_warning_silent,
  };
}

function toUpdateBody(values: FormValues): AdminNotificationSettingsUpdateBody {
  const body: AdminNotificationSettingsUpdateBody = {
    smtp_host: values.smtp_host.trim(),
    smtp_port: Number(values.smtp_port),
    from_address: values.from_address.trim(),
    recipients: normalizeRecipients(values.recipients),
    auth_username: values.auth_username.trim(),
    clear_smtp_auth_password: values.clear_smtp_auth_password,
    require_tls: values.require_tls,
    tls_min_version: values.tls_min_version,
    telegram_chat_id: values.telegram_chat_id.trim(),
    clear_telegram_bot_token: values.clear_telegram_bot_token,
    telegram_warning_silent: values.telegram_warning_silent,
  };
  const smtpPassword = values.smtp_auth_password.trim();
  if (!values.clear_smtp_auth_password && smtpPassword) {
    body.smtp_auth_password = smtpPassword;
  }
  const botToken = values.telegram_bot_token.trim();
  if (!values.clear_telegram_bot_token && botToken) {
    body.telegram_bot_token = botToken;
  }
  return body;
}

function validate(values: FormValues): string | null {
  const port = Number(values.smtp_port);
  if (!Number.isInteger(port) || port < 1 || port > 65535) {
    return "Port must be between 1 and 65535.";
  }
  if (values.from_address.trim() && !values.from_address.includes("@")) {
    return "From address must be an email address.";
  }
  const recipients = recipientList(values.recipients);
  if (values.recipients.trim() && recipients.some((recipient) => !recipient.includes("@"))) {
    return "Every receiver must be an email address.";
  }
  const chatId = values.telegram_chat_id.trim();
  if (chatId && !CHAT_ID_PATTERN.test(chatId)) {
    return "Telegram chat ID must be numeric.";
  }
  return null;
}

function recipientList(value: string): string[] {
  return value
    .replaceAll("\n", ",")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeRecipients(value: string): string {
  return recipientList(value).join(", ");
}

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString();
}

function severityColor(severity: string): string {
  if (severity === "critical") {
    return "red";
  }
  if (severity === "warning") {
    return "yellow";
  }
  return "blue";
}
